declare global {
  interface Window {
    argon2: {
      ArgonType: {
        Argon2d: number;
        Argon2i: number;
        Argon2id: number;
      };
      hash: (options: {
        pass: string | Uint8Array;
        salt: string | Uint8Array;
        type?: number;
        mem?: number;
        time?: number;
        parallelism?: number;
        hashLen?: number;
      }) => Promise<{ hash: Uint8Array; hashHex: string; encoded: string }>;
      verify: (options: {
        pass: string | Uint8Array;
        hash: string;
        type?: number;
      }) => Promise<void>;
      unloadRuntime: () => void;
    };
  }
}

export interface Argon2Params {
  memory: number;
  iterations: number;
  parallelism: number;
  hashLength: number;
}

const DEFAULT_PARAMS: Argon2Params = {
  memory: 65536,
  iterations: 3,
  parallelism: 4,
  hashLength: 32
};

function getArgon2() {
  if (typeof window === 'undefined' || !window.argon2) {
    throw new Error('argon2 not loaded');
  }
  return window.argon2;
}

export async function deriveWrapKey(
  passphrase: string,
  existingSalt?: Uint8Array
): Promise<{ wrapKey: ArrayBuffer; salt: Uint8Array }> {
  const argon2 = getArgon2();
  const salt = existingSalt ?? new Uint8Array(16);
  if (!existingSalt) {
    crypto.getRandomValues(salt);
  }

  const result = await argon2.hash({
    pass: passphrase,
    salt,
    type: argon2.ArgonType.Argon2id,
    mem: DEFAULT_PARAMS.memory,
    time: DEFAULT_PARAMS.iterations,
    parallelism: DEFAULT_PARAMS.parallelism,
    hashLen: DEFAULT_PARAMS.hashLength
  });

  return {
    wrapKey: result.hash.buffer as ArrayBuffer,
    salt
  };
}

export async function verifyWrapKey(
  passphrase: string,
  hashHex: string,
  _salt: Uint8Array
): Promise<boolean> {
  const argon2 = getArgon2();
  try {
    await argon2.verify({
      pass: passphrase,
      hash: hashHex,
      type: argon2.ArgonType.Argon2id
    });
    return true;
  } catch {
    return false;
  }
}

export async function importWrapKey(keyData: ArrayBuffer): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'AES-GCM' },
    false,
    ['wrapKey', 'unwrapKey', 'encrypt', 'decrypt']
  );
}